import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import PersonRounded from "@mui/icons-material/PersonRounded";
import SportsEsportsRounded from "@mui/icons-material/SportsEsportsRounded";
import { divisionMappingsSimple, mapMappings } from "../../Constants";
import { AccountBadges } from "../../components/Badges/AccountBadges";
import Loading from "../../components/Loading";
import PlayerAvatar from "../../components/PlayerAvatar";
import SiteBanner from "../../components/SiteBanner/SiteBanner";
import { AuthContext } from "../../context/AuthContext";
import { Account } from "../../models/Account";
import { LeaderboardGame, LeaderboardGameStats } from "../../models/LeaderboardGame";
import { LeaderboardPlayer } from "../../models/LeaderboardPlayer";
import {
    getDivisionsBySeason,
    getLatestSeason,
    getOldestSeason,
} from "../../services/MatchService";
import { fetchTop10Players, fetchTopGames } from "../../services/PlayerStatService";
import { useBadges } from "../../services/BadgeService";
import { TournamentConfig, getTournamentConfigsBySeason } from "../../services/TournamentService";
import { getDivisionDisplayName } from "../../utils/divisionUtils";
import styles from "./LeaderboardPage.module.css";

/** Season the T/CT split became available. */
const SEASON_WITH_SIDES = 29;

/**
 * First season with Rating 3.0 data. Season 32 was reparsed when R3 shipped; 29-31 have no R3
 * at all, so they stay on 2.1. Must match SEASON_WITH_RATING30 in LeaderboardCacheService.cs —
 * the API keys its response dictionary by these stat names.
 */
const FIRST_R3_SEASON = 32;

/**
 * First season with per-map stats; older seasons only have season totals. Must match
 * FIRST_GAMES_SEASON in LeaderboardCacheService.cs.
 */
const FIRST_GAMES_SEASON = 26;

/**
 * The Games view only ranks these on maps with at least this many kills. Must match
 * GAMES_KD_MIN_KILLS / GAMES_HS_MIN_KILLS in LeaderboardCacheService.cs.
 */
const GAMES_MIN_KILLS: Record<string, number> = { KD: 10, HS: 15 };

/** How many ranked rows the API returns per stat; anything beyond is the personal row. */
const TOP_N = 10;

type Side = "Overall" | "T" | "CT";

/** Season averages per player, or the best single maps. Kept in the URL as ?view=games. */
type View = "players" | "games";

/** One ranked line as a card draws it, whichever view it came from. */
interface CardEntry {
    placement: number;
    steamId: string;
    account: Account | null | undefined;
    isCurrentUser: boolean;
    value: string;
    division: string | null | undefined;
    /** The player's team (players view) or the map and opponent (games view). */
    detail: (leader: boolean) => React.ReactNode;
}

const STAT_LABELS: Record<string, string> = {
    KD: "K/D",
    Rating2: "Rating 2.0",
    Rating21: "Rating 2.1",
    Rating30: "Rating 3.0",
    ADR: "ADR",
    HS: "HS%",
    KAST: "KAST%",
    openingDuelWinPercentage: "Opening Duel Win%",
    Kills: "Kills",
    OpeningKills: "Opening Kills",
};

/** Stats formatted to two decimals, counts shown whole; the rest are percentages shown to one. */
const TWO_DECIMAL_STATS = ["KD", "Rating2", "Rating21", "Rating30", "ADR"];
const COUNT_STATS = ["Kills", "OpeningKills"];

const formatValue = (stat: string, value: number | null | undefined): string => {
    if (value === undefined || value === null) return "—";
    if (COUNT_STATS.includes(stat)) return String(value);
    return value.toFixed(TWO_DECIMAL_STATS.includes(stat) ? 2 : 1);
};

/** A division as the dropdown offers it: one entry per division, groups folded in. */
interface DivisionOption {
    /** The prefix the API filters on with StartsWith, e.g. "2div" or "liga-kval-1". */
    division: string;
    label: string;
    groups: string[];
}

/**
 * The API returns side-specific values under prefixed properties (`t_KD`, `cT_Rating21`, …).
 * Note the odd casing on the CT keys — that is what the serializer produces, don't "fix" it.
 */
const statValue = (stat: string, player: any, side?: "T" | "CT"): number | undefined => {
    const key =
        side === "T" ? `t_${stat}`
        : side === "CT" ? `cT_${stat}`
        : stat === "KD" ? "kd"
        : stat === "ADR" ? "adr"
        : stat === "HS" ? "hs"
        : stat === "KAST" ? "kast"
        : stat === "Rating2" ? "rating2"
        : stat === "Rating21" ? "rating21"
        : stat === "Rating30" ? "rating30"
        : stat;
    const value = player?.[key];
    return typeof value === "number" ? value : undefined;
};

const formatStat = (stat: string, player: any, side?: "T" | "CT"): string =>
    formatValue(stat, statValue(stat, player, side));

const gameStatValue = (stat: string, game: LeaderboardGameStats): number | null => {
    switch (stat) {
        case "KD": return game.kd;
        case "Rating2": return game.rating2;
        case "Rating21": return game.rating21;
        case "Rating30": return game.rating30;
        case "ADR": return game.adr;
        case "HS": return game.hs;
        case "Kills": return game.kills;
        case "OpeningKills": return game.openingKills;
        default: return null;
    }
};

/** de_mirage* -> Mirage; unknown maps lose the de_ prefix and get a capital. */
const mapLabel = (mapName: string | null): string => {
    if (!mapName) return "";
    const clean = mapName.replace("*", "");
    const known = mapMappings.get(clean);
    if (known) return known;
    const bare = clean.replace(/^de_/, "");
    return bare.charAt(0).toUpperCase() + bare.slice(1);
};

/** The match page opens on the map named by ?map=mirage (see MatchPage2's mapSlug). */
const gameUrl = (game: LeaderboardGameStats): string => {
    const slug = (game.mapName ?? "").replace("*", "").replace(/^de_/, "").toLowerCase();
    return slug ? `/match/${game.matchId}?map=${slug}` : `/match/${game.matchId}`;
};

/** Team crests are stored without a width, so ask the CDN for one the size we actually render. */
const crestUrl = (logo: string, width: number): string =>
    !logo || logo.includes("width=") ? logo : `${logo}&width=${width}`;

const Chevron = () => (
    <svg className={styles.chevron} width="12" height="12" viewBox="0 0 24 24" fill="none"
        strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="6 9 12 15 18 9" />
    </svg>
);

const LeaderboardPage: React.FC = () => {
    const { t } = useTranslation(["pages", "common", "stats", "errors"]);
    const { user: visitor } = useContext(AuthContext);
    const badges = useBadges();
    const [searchParams, setSearchParams] = useSearchParams();
    const view: View = searchParams.get("view") === "games" ? "games" : "players";

    const [playerStats, setPlayerStats] = useState<Record<string, LeaderboardPlayer[]>>({});
    const [gameStats, setGameStats] = useState<Record<string, LeaderboardGame[]>>({});
    const [seasons, setSeasons] = useState<number[]>([]);
    const [currentSeason, setCurrentSeason] = useState<number>(0);
    const [divisions, setDivisions] = useState<DivisionOption[]>([]);
    const [tournamentConfigs, setTournamentConfigs] = useState<TournamentConfig[]>([]);
    const [currentDivision, setCurrentDivision] = useState<string>("");
    const [currentGroup, setCurrentGroup] = useState<string | null>(null);
    const [currentSide, setCurrentSide] = useState<Side>("Overall");
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>("");

    // Depend on the id, not the object: AuthContext replaces `user`'s identity when it refreshes
    // the account, which would otherwise re-fetch the whole leaderboard two extra times per load.
    const visitorSteamId = visitor?.steamID ?? null;

    // Games keeps K/D, rating, ADR and HS% in the same slots so a switch compares like with like.
    // KAST% and opening duel win% tie at 100% on hundreds of single maps, so they make way.
    const statList = useMemo(() => {
        const rating =
            currentSeason >= FIRST_R3_SEASON ? "Rating30"
            : currentSeason >= SEASON_WITH_SIDES ? "Rating21"
            : "Rating2";
        return view === "games"
            ? ["KD", rating, "ADR", "HS", "Kills", "OpeningKills"]
            : ["KD", rating, "ADR", "HS", "KAST", "openingDuelWinPercentage"];
    }, [currentSeason, view]);

    // Per-map T/CT numbers are a dozen rounds each, so the side split is a players-only filter.
    const supportsSides = view === "players" && currentSeason >= SEASON_WITH_SIDES;
    const sideParam = supportsSides && currentSide !== "Overall" ? currentSide : undefined;
    const gamesUnavailable = view === "games" && currentSeason > 0 && currentSeason < FIRST_GAMES_SEASON;

    const activeDivision = divisions.find((d) => d.division === currentDivision);

    // Load the season range once.
    useEffect(() => {
        const loadSeasons = async () => {
            try {
                const [latest, oldest] = await Promise.all([getLatestSeason(), getOldestSeason()]);
                setSeasons(
                    Array.from({ length: latest - oldest + 1 }, (_, i) => oldest + i).sort((a, b) => b - a)
                );
                setCurrentSeason(latest);
            } catch (err) {
                setError(err instanceof Error ? err.message : String(err));
                setLoading(false);
            }
        };
        loadSeasons();
    }, []);

    // Divisions for the selected season. Lettered divisions (2divA/2divB) collapse into one entry
    // with a group dropdown, because the API filters division by StartsWith and group by EndsWith.
    const loadDivisions = useCallback(async () => {
        if (!currentSeason) return;
        try {
            const [raw, configs] = await Promise.all([
                getDivisionsBySeason(currentSeason),
                getTournamentConfigsBySeason(currentSeason).catch(() => [] as TournamentConfig[]),
            ]);
            setTournamentConfigs(configs);

            // Playoff/Relegation/unknown are bookkeeping values, not divisions players are ranked in.
            const skip = ["playoff", "relegation", "unknown"];
            const options = new Map<string, DivisionOption>();

            raw.filter((d) => d && !skip.includes(d.toLowerCase())).forEach((division) => {
                const lettered = division.match(/^(\d+div)([A-Z])$/);
                if (lettered) {
                    const [, prefix, letter] = lettered;
                    const existing = options.get(prefix);
                    if (existing) {
                        if (!existing.groups.includes(letter)) existing.groups.push(letter);
                    } else {
                        options.set(prefix, {
                            division: prefix,
                            label: divisionMappingsSimple.get(prefix) ?? prefix,
                            groups: [letter],
                        });
                    }
                    return;
                }
                if (!options.has(division)) {
                    options.set(division, {
                        division,
                        label: getDivisionDisplayName(division, configs),
                        groups: [],
                    });
                }
            });

            const order = (division: string) => {
                if (division === "ligaen") return 0;
                const numbered = division.match(/^(\d+)div/);
                if (numbered) return Number(numbered[1]);
                return 90; // qualifiers and other tournament divisions last
            };
            const list = Array.from(options.values());
            list.forEach((o) => o.groups.sort());
            list.sort((a, b) => order(a.division) - order(b.division) || a.label.localeCompare(b.label));
            setDivisions(list);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        }
    }, [currentSeason]);

    useEffect(() => {
        loadDivisions();
    }, [loadDivisions]);

    useEffect(() => {
        if (!currentSeason) return;
        if (gamesUnavailable) {
            setGameStats({});
            setLoading(false);
            return;
        }

        // A switch of view or season can overtake a slower request; only the latest one lands.
        let cancelled = false;
        setLoading(true);
        setError("");
        const division = currentDivision || null;
        const load = view === "games"
            ? fetchTopGames(currentSeason, visitorSteamId, division, currentGroup).then((games) => {
                if (!cancelled) setGameStats(games);
            })
            : fetchTop10Players(currentSeason, visitorSteamId, division, currentGroup, sideParam).then((players) => {
                if (!cancelled) setPlayerStats(players);
            });
        load
            .catch((err) => {
                if (!cancelled) setError(err instanceof Error ? err.message : String(err));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [view, gamesUnavailable, currentSeason, currentDivision, currentGroup, sideParam, visitorSteamId]);

    const selectView = (next: View) => {
        const params = new URLSearchParams(searchParams);
        if (next === "games") params.set("view", "games");
        else params.delete("view");
        setSearchParams(params, { replace: true });
    };

    const selectSeason = (season: number) => {
        setCurrentSeason(season);
        setCurrentDivision("");
        setCurrentGroup(null);
    };

    const selectDivision = (division: string) => {
        setCurrentDivision(division);
        setCurrentGroup(null);
    };

    const isEmpty = view === "games"
        ? statList.every((stat) => !(gameStats[stat]?.length))
        : statList.every((stat) => !(playerStats[stat]?.length));

    const renderName = (steamId: string, account: Account | null | undefined, isCurrentUser: boolean) => {
        const name = account ? account.playerName : steamId;
        return (
            <>
                <Link to={`/player/${steamId}`} className={styles.nameLink}>{name}</Link>
                <AccountBadges account={account ?? null} badges={badges} />
                {isCurrentUser && <span className={styles.youBadge}>{t("common:you")}</span>}
            </>
        );
    };

    const renderTeam = (entry: LeaderboardPlayer, leader = false) => {
        const team = entry.stats.team;
        if (!team) return null;
        return (
            <span className={styles.teamCell}>
                <span className={styles.teamName}>{team.teamName}</span>
                {team.teamLogo && (
                    <img
                        className={leader ? `${styles.crest} ${styles.crestLeader}` : styles.crest}
                        src={crestUrl(team.teamLogo, 32)}
                        alt=""
                        loading="lazy"
                    />
                )}
            </span>
        );
    };

    /** Map, score and opponent of one game, linking to that map on the match page. */
    const renderGame = (game: LeaderboardGameStats, leader = false) => {
        const map = mapLabel(game.mapName);
        const hasScore = game.teamScore !== null && game.opponentScore !== null;
        const score = hasScore ? `${game.teamScore}–${game.opponentScore}` : "";
        return (
            <Link
                to={gameUrl(game)}
                className={leader ? `${styles.gameCell} ${styles.gameCellLeader}` : styles.gameCell}
                title={[map, score, game.opponentName && `${t("common:vs")} ${game.opponentName}`].filter(Boolean).join(" ")}
            >
                <span className={styles.gameMap}>{map}</span>
                {hasScore && <span className={game.won ? styles.scoreWon : styles.scoreLost}>{score}</span>}
                <span className={styles.gameVs}>{t("common:vs")}</span>
                {game.opponentLogo && (
                    <img className={styles.crest} src={crestUrl(game.opponentLogo, 32)} alt="" loading="lazy" />
                )}
                <span className={styles.teamName}>{game.opponentName}</span>
            </Link>
        );
    };

    const cardEntries = (stat: string): CardEntry[] =>
        view === "games"
            ? (gameStats[stat] ?? []).map((entry) => ({
                placement: entry.placement,
                steamId: entry.game.steamId,
                account: entry.game.account,
                isCurrentUser: entry.isCurrentUser,
                value: formatValue(stat, gameStatValue(stat, entry.game)),
                division: entry.game.division,
                detail: (leader: boolean) => renderGame(entry.game, leader),
            }))
            : (playerStats[stat] ?? []).map((entry) => ({
                placement: entry.placement,
                steamId: entry.stats.steamID,
                account: entry.stats.account,
                isCurrentUser: entry.isCurrentUser,
                value: formatStat(stat, entry.stats, sideParam),
                division: entry.stats.team?.division,
                detail: (leader: boolean) => renderTeam(entry, leader),
            }));

    const renderRow = (entry: CardEntry, index: number, standalone = false) => {
        const rowTone = index % 2 === 0 ? styles.rowEven : styles.rowOdd;
        return (
            <div
                key={`${entry.steamId}-${entry.placement}`}
                className={`${styles.row} ${standalone ? "" : rowTone} ${entry.isCurrentUser ? styles.youRow : ""}`}
            >
                <span className={styles.rank}>{entry.placement}</span>
                <PlayerAvatar
                    steamId={entry.steamId}
                    name={entry.account?.playerName}
                    size={24}
                    badges={badges}
                />
                <span className={styles.nameCell}>{renderName(entry.steamId, entry.account, entry.isCurrentUser)}</span>
                {entry.detail(false)}
                <span className={styles.value}>{entry.value}</span>
            </div>
        );
    };

    const renderCard = (stat: string) => {
        const entries = cardEntries(stat);
        if (entries.length === 0) return null;

        const ranked = entries.slice(0, TOP_N);
        // The API appends the signed-in player after the ranked ten when they didn't make it, so
        // anything past TOP_N is that one personal row — see LeaderboardCacheService.
        const personal = entries.length > TOP_N ? entries[TOP_N] : null;
        const [leader, ...rest] = ranked;

        const minKills = view === "games" ? GAMES_MIN_KILLS[stat] : undefined;
        const meta = minKills
            ? `${t("pages:leaderboard.top10")} · ${t("pages:leaderboard.minKills", { count: minKills })}`
            : t("pages:leaderboard.top10");

        return (
            <div className={styles.card} key={stat}>
                <div className={styles.cardHead}>
                    <span className={styles.cardTitle}>{STAT_LABELS[stat] ?? stat}</span>
                    <span className={styles.cardMeta}>{meta}</span>
                </div>

                <div className={`${styles.leader} ${leader.isCurrentUser ? styles.youRow : ""}`}>
                    <PlayerAvatar
                        steamId={leader.steamId}
                        name={leader.account?.playerName}
                        size={54}
                        ring="rgba(255, 102, 0, 0.45)"
                        badges={badges}
                    />
                    <div className={styles.leaderBody}>
                        <div className={styles.leaderName}>
                            {renderName(leader.steamId, leader.account, leader.isCurrentUser)}
                        </div>
                        <div className={styles.leaderMeta}>
                            {leader.detail(true)}
                            {!currentDivision && leader.division && (
                                <span className={styles.divChip}>
                                    {getDivisionDisplayName(leader.division, tournamentConfigs)}
                                </span>
                            )}
                        </div>
                    </div>
                    <span className={styles.leaderValue}>{leader.value}</span>
                </div>

                <div className={styles.rows}>
                    {rest.map((entry, index) => renderRow(entry, index))}

                    {personal && (
                        <>
                            <div className={styles.youSplit}>
                                <span className={styles.youSplitLabel}>
                                    {view === "games"
                                        ? t("pages:leaderboard.yourBestGame")
                                        : t("pages:leaderboard.yourPosition")}
                                </span>
                                <span className={styles.youSplitRule} />
                            </div>
                            {renderRow(personal, 0, true)}
                        </>
                    )}
                </div>
            </div>
        );
    };

    const renderDropdown = (
        id: string,
        widthClass: string,
        label: string,
        current: string,
        items: { key: string; label: string; active: boolean; onSelect: () => void }[]
    ) => (
        <div className={`dropdown ${styles.field} ${widthClass}`}>
            <label className={styles.fieldLabel} htmlFor={id}>{label}</label>
            <button
                className={styles.toggle}
                type="button"
                id={id}
                data-bs-toggle="dropdown"
                aria-expanded="false"
            >
                <span className={styles.toggleText}>{current}</span>
                <Chevron />
            </button>
            <ul className={`dropdown-menu ${styles.menu}`} aria-labelledby={id}>
                {items.map((item) => (
                    <li key={item.key}>
                        <button
                            className={`${styles.menuItem} ${item.active ? styles.menuItemActive : ""}`}
                            type="button"
                            onClick={item.onSelect}
                        >
                            {item.label}
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    );

    return (
        <div>
            <SiteBanner />
            <div className={styles.page}>
                <div className={styles.header}>
                    <div className={styles.headerText}>
                        <h1 className={styles.title}>{t("pages:leaderboard.title")}</h1>
                        <p className={styles.description}>
                            {view === "games"
                                ? t("pages:leaderboard.gamesDescription")
                                : t("pages:leaderboard.description")}
                        </p>
                    </div>
                    <div className={styles.viewSwitch} role="group" aria-label={t("pages:leaderboard.viewAria")}>
                        {([
                            ["players", t("pages:leaderboard.viewPlayers"), <PersonRounded key="icon" />],
                            ["games", t("pages:leaderboard.viewGames"), <SportsEsportsRounded key="icon" />],
                        ] as [View, string, React.ReactNode][]).map(([option, label, icon]) => (
                            <button
                                key={option}
                                type="button"
                                aria-pressed={view === option}
                                className={`${styles.viewOption} ${view === option ? styles.viewOptionActive : ""}`}
                                onClick={() => selectView(option)}
                            >
                                {icon}
                                {label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className={styles.filterBar}>
                    {renderDropdown(
                        "leaderboardSeason",
                        styles.fieldSeason,
                        t("common:selectSeason"),
                        currentSeason ? t("common:seasonNum", { number: currentSeason }) : t("common:selectSeason"),
                        seasons.map((season) => ({
                            key: String(season),
                            label: t("common:seasonNum", { number: season }),
                            active: season === currentSeason,
                            onSelect: () => selectSeason(season),
                        }))
                    )}

                    {renderDropdown(
                        "leaderboardDivision",
                        styles.fieldDivision,
                        t("common:selectDivision"),
                        activeDivision ? activeDivision.label : t("common:all"),
                        [
                            {
                                key: "all",
                                label: t("common:all"),
                                active: !currentDivision,
                                onSelect: () => selectDivision(""),
                            },
                            ...divisions.map((division) => ({
                                key: division.division,
                                label: division.label,
                                active: division.division === currentDivision,
                                onSelect: () => selectDivision(division.division),
                            })),
                        ]
                    )}

                    {activeDivision && activeDivision.groups.length > 0 && renderDropdown(
                        "leaderboardGroup",
                        styles.fieldGroup,
                        t("common:selectGroup"),
                        currentGroup ?? t("common:all"),
                        [
                            {
                                key: "all",
                                label: t("common:all"),
                                active: currentGroup === null,
                                onSelect: () => setCurrentGroup(null),
                            },
                            ...activeDivision.groups.map((group) => ({
                                key: group,
                                label: group,
                                active: group === currentGroup,
                                onSelect: () => setCurrentGroup(group),
                            })),
                        ]
                    )}

                    {supportsSides && (
                        <>
                            <span className={styles.barDivider} />
                            <div className={styles.field}>
                                <span className={styles.fieldLabel}>{t("stats:side", "Side")}</span>
                                <div className={styles.segments}>
                                    {([
                                        ["Overall", t("stats:overall")],
                                        ["T", t("stats:tSide")],
                                        ["CT", t("stats:ctSide")],
                                    ] as [Side, string][]).map(([side, label]) => (
                                        <button
                                            key={side}
                                            type="button"
                                            className={`${styles.segment} ${currentSide === side ? styles.segmentActive : ""}`}
                                            onClick={() => setCurrentSide(side)}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {loading ? (
                    <div className="text-center" style={{ paddingTop: 60 }}><Loading /></div>
                ) : error ? (
                    <div className={styles.empty}>
                        <p className={styles.emptyTitle}>{t("errors:generic.fetchError")}</p>
                    </div>
                ) : gamesUnavailable ? (
                    <div className={styles.empty}>
                        <p className={styles.emptyTitle}>{t("pages:leaderboard.noGamesTitle")}</p>
                        <p className={styles.emptyText}>
                            {t("pages:leaderboard.noGamesText", { season: FIRST_GAMES_SEASON })}
                        </p>
                    </div>
                ) : isEmpty ? (
                    <div className={styles.empty}>
                        <p className={styles.emptyTitle}>{t("pages:leaderboard.noDataTitle")}</p>
                        <p className={styles.emptyText}>{t("pages:leaderboard.noDataText")}</p>
                    </div>
                ) : (
                    <div className={styles.grid}>
                        {statList.map((stat) => renderCard(stat))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default LeaderboardPage;
